import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { CopyableLinkCell } from '../../components/ui/CopyableLinkCell';
import { TypeDeleteConfirmationModal } from '../../components/ui/TypeDeleteConfirmationModal';
import { useClickOutside } from '../../lib/useClickOutside';
import { linkifyText } from '../../lib/linkifyText';
import { formatSydneyDateTime } from '../../lib/sydneyDateTime';
import { ApiError } from '../../lib/api';
import type { PromotionPlanStatus } from '../../types/promotionPlan';
import { getAccountDetail } from '../accounts/api';
import { changePlanStatus, getPlan, hardDeletePlan } from './api';
import { planStatusBadge } from './PlansPage';
import { PlanTasks } from './PlanTasks';
import { CampaignsAttachedSection } from './CampaignsAttached';
import { PlanRolloutPanel } from './PlanRolloutPanel';
import { PlanDashboardStats } from './PlanDashboardStats';
import { PlanActivity } from './PlanActivity';
import { PlanThread } from './PlanThread';

const PLAN_STATUS_OPTIONS: PromotionPlanStatus[] = [
  'draft',
  'active',
  'completed',
  'archived',
];

// Spec §5.4/§5.5: the header status pill is a dropdown of all four states;
// every transition is allowed in any direction in Slice A (no gate).
function PlanStatusPill({
  planId,
  status,
}: {
  planId: string;
  status: PromotionPlanStatus;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const mut = useMutation({
    mutationFn: (next: PromotionPlanStatus) =>
      changePlanStatus(planId, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan', planId] });
      qc.invalidateQueries({ queryKey: ['plans'] });
    },
  });

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={mut.isPending}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`text-xs px-2 py-0.5 rounded-full ${planStatusBadge(status)} disabled:opacity-50`}
      >
        {status} ▾
      </button>
      {open && (
        <div className="absolute left-0 z-10 mt-1 w-36 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {PLAN_STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setOpen(false);
                if (s !== status) mut.mutate(s);
              }}
              className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 ${
                s === status
                  ? 'font-semibold text-slate-900'
                  : 'text-slate-600'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="bg-white rounded-md border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 py-1.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900">
        {value ?? <span className="text-slate-400">—</span>}
      </dd>
    </div>
  );
}


export function PlanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const planQuery = useQuery({
    queryKey: ['plan', id],
    queryFn: () => getPlan(id!),
    enabled: !!id,
  });

  const brandId = planQuery.data?.brandAccountId;
  const brandQuery = useQuery({
    queryKey: ['account', brandId],
    queryFn: () => getAccountDetail(brandId!),
    enabled: !!brandId,
  });

  const deleteMut = useMutation({
    mutationFn: () => hardDeletePlan(id!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['plans'] });
      qc.removeQueries({ queryKey: ['plan', id] });
      navigate('/plans');
    },
    onError: (err) =>
      setDeleteError(err instanceof ApiError ? err.message : String(err)),
  });

  if (planQuery.isLoading) return <CenteredSpinner />;
  if (planQuery.error || !planQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Plan not found or failed to load.
      </div>
    );
  }

  const p = planQuery.data;
  const brand = brandQuery.data?.account ?? null;
  // Hidden entirely when empty (spec §5.3). Legacy plans predate the field
  // and read as undefined -> also hidden.
  const overviewText =
    p.overview && p.overview.trim().length > 0 ? p.overview : null;
  const dateRange =
    p.promoStartDate || p.promoEndDate
      ? `${formatSydneyDateTime(p.promoStartDate)} — ${formatSydneyDateTime(p.promoEndDate)}`
      : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-500">
            <Link to="/plans" className="hover:underline">
              Plans
            </Link>
            {brand && (
              <>
                <span className="mx-1 text-slate-300">/</span>
                <Link
                  to={`/accounts/${brand.id}`}
                  className="hover:underline"
                >
                  {brand.name}
                </Link>
              </>
            )}
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">{p.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <PlanStatusPill planId={p.id} status={p.status} />
            {dateRange && (
              <span className="text-sm text-slate-500">
                {brand ? `${brand.name} · ` : ''}
                {dateRange}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/plans/${p.id}/edit`}>
            <Button type="button">Edit</Button>
          </Link>
          <span
            title={
              p.rolloutId
                ? 'Delete the Rollout first before deleting this Plan.'
                : undefined
            }
            className="inline-block"
          >
            <Button
              type="button"
              variant="outline"
              disabled={!!p.rolloutId}
              onClick={() => {
                setDeleteError(null);
                setShowDelete(true);
              }}
              className="!border-red-300 !text-red-600 hover:!bg-red-50"
            >
              Delete Plan
            </Button>
          </span>
        </div>
      </div>

      {showDelete && (
        <TypeDeleteConfirmationModal
          entityType="Plan"
          entityName={p.name}
          impactMessage="Its tasks are permanently deleted too. Attached Campaigns are not deleted."
          busy={deleteMut.isPending}
          onCancel={() => setShowDelete(false)}
          onConfirm={() => deleteMut.mutate()}
        />
      )}
      {deleteError && (
        <p className="text-sm text-red-600">{deleteError}</p>
      )}

      {overviewText && (
        <section className="bg-white rounded-md border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">
            Overview
          </h2>
          <p className="whitespace-pre-wrap text-sm text-slate-700">
            {linkifyText(overviewText)}
          </p>
        </section>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <Section title="Campaigns">
            <CampaignsAttachedSection plan={p} />
          </Section>

          <Section title="Tasks">
            <PlanTasks planId={p.id} plan={p} />
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Offer details">
            <dl>
              <Field
                label="Offer mechanics"
                value={
                  <CopyableLinkCell
                    href={p.offerMechanicsUrl}
                    display={<span>Open</span>}
                    ariaLabel="Offer mechanics URL"
                  />
                }
              />
              <Field
                label="Kyck overview"
                value={
                  <CopyableLinkCell
                    href={p.kyckCampaignOverviewUrl}
                    display={<span>Open</span>}
                    ariaLabel="Kyck campaign overview URL"
                  />
                }
              />
              <Field
                label="Promo start"
                value={formatSydneyDateTime(p.promoStartDate)}
              />
              <Field
                label="Promo end"
                value={formatSydneyDateTime(p.promoEndDate)}
              />
            </dl>
            {p.datesLocked && (
              <p className="mt-3 text-xs text-slate-500">
                Dates locked — a Rollout exists. Edit dates on the Rollout.
              </p>
            )}
          </Section>

          <Section title="Stats">
            <PlanDashboardStats planId={p.id} />
          </Section>

          <Section title="Rollout">
            <PlanRolloutPanel plan={p} />
          </Section>
        </div>
      </div>

      <Section title="Thread">
        <PlanThread planId={p.id} />
      </Section>

      <Section title="Activity">
        <PlanActivity planId={p.id} />
      </Section>
    </div>
  );
}
